/**
 * ClientsFormController — page `clients-edition.html` (création) et
 * `clients-edition.html?id={id}` (édition) (EPIC-009 / 9.5).
 *
 * Pilote la création + l'édition d'un client en pattern « édition par
 * bloc » (calque `parametres.html` / `settings_payment_conditions`).
 *
 *   - Mode CRÉATION (`clients-edition.html`) : seuls les blocs Identité +
 *     Adresse facturation sont visibles, en mode édition direct (pas
 *     de résumé). Un bouton global « Créer ce client » orchestre :
 *       1. POST /clients     (core : type + legalName + siret|vatIntracom)
 *       2. POST /clients/{id}/addresses (type=billing, isDefault=true)
 *       3. POST /clients/{id}/contacts  (primaire, b2c uniquement)
 *     Sur succès → redirige vers `clients-edition.html?id={id}` pour permettre
 *     l'ajout des autres blocs (livraison, contacts additionnels).
 *
 *   - Mode ÉDITION (`clients-edition.html?id={id}`) : `GET /clients/{id}`
 *     hydrate tous les blocs. Chaque bloc a son cycle Modifier →
 *     Form → Enregistrer/Annuler ; le submit du bloc ne tape qu'un
 *     seul endpoint :
 *       - Identité   → PATCH /clients/{id}
 *       - Facturation → PATCH /clients/{id}/addresses/{billingId}
 *       - Livraison   → POST | PATCH | POST /archive selon le toggle
 *       - Contact primaire → PATCH (existe) | POST (création tardive)
 *       - Contact additionnel → POST | PATCH | POST /archive
 *
 * Validation côté client :
 *   - legalName obligatoire,
 *   - SIRET (Luhn 14 chiffres) obligatoire pour b2b / public_administration,
 *   - email primaire obligatoire pour b2c (optionnel pour les autres).
 *
 * Erreurs 422 backend (cf. 9.2) :
 *   `{ error: { errors: [{ field, message }] } }`
 * → dispatchées sur les inputs du bloc concerné via aria-invalid +
 * `<p class="nm-field__error" role="alert">`. Sur 404 / 409, message
 * générique en bandeau du bloc.
 *
 * Convention CLAUDE.md respectée : pas de HTML inline en JS — les
 * lignes contact additionnel sont clonées depuis `<template
 * id="tpl-contact-block">`. Aucune classe inventée.
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";
import { searchEntreprises, findEstablishmentBySiret } from "/js/services/entreprises.js";

const TYPE_LABELS_FR = {
  b2b: "Entreprise",
  b2c: "Particulier",
  public_administration: "Administration",
};

const COUNTRY_LABELS_FR = {
  FR: "France",
  BE: "Belgique",
  LU: "Luxembourg",
  CH: "Suisse",
  DE: "Allemagne",
};

export default class ClientsFormController extends BaseController {
  static targets = [
    "crumb", "crumbClient", "crumbSep",
    "overline", "title", "subtitle", "modeLabel", "loadError",

    // Bloc Identité
    "identityBlock", "identityOpenBtn", "identityCancelBtn", "identitySubmitBtn",
    "identitySummary",
    "typeInput", "typeError",
    "legalNameLabel", "legalNameInput", "legalNameError",
    "siretField", "siretInput", "siretError",
    "vatField", "vatInput", "vatError",
    "identityRefetchRow", "identityRefetchBtn",

    // Bloc Remplissage automatique (recherche-entreprises.api.gouv.fr)
    "autofillBlock", "autofillInput", "autofillSuggestList",

    // Bloc Facturation
    "billingBlock", "billingOpenBtn", "billingCancelBtn", "billingSubmitBtn",
    "billingSummary",
    "billingLine1Input", "billingLine1Error",
    "billingLine2Input",
    "billingPostalInput", "billingPostalError",
    "billingCityInput", "billingCityError",
    "billingCountryInput",
    "billingRefetchRow", "billingRefetchBtn",

    // Actions création (bouton global)
    "createActions", "createSubmitBtn", "createError",

    // Bloc Livraison
    "shippingBlock", "shippingOpenBtn", "shippingSubmitBtn", "shippingSummary",
    "shippingSameInput", "shippingFields",
    "shippingLine1Input", "shippingLine1Error",
    "shippingLine2Input",
    "shippingPostalInput", "shippingPostalError",
    "shippingCityInput", "shippingCityError",
    "shippingCountryInput",

    // Bloc Contact principal
    "primaryBlock", "primaryOpenBtn", "primarySubmitBtn", "primarySummary",
    "primaryEmailStar", "primaryEmailInput", "primaryEmailError", "primaryPhoneInput",

    // Contacts additionnels (liste + bloc « ajouter »)
    "contactsList",
    "newContactBlock", "newContactOpenBtn", "newContactSubmitBtn",
    "newContactFirstNameInput", "newContactLastNameInput",
    "newContactFunctionInput",
    "newContactEmailInput", "newContactEmailError",
  ];

  connect() {
    this._parseMode();
    this._wireFor(this._mode);
  }

  _parseMode() {
    // Forme canonique (EPIC-099.53) : clients-edition.html?id={uuid} en
    // édition, clients-edition.html sans paramètre en création.
    const params = new URLSearchParams(window.location.search);
    const id = params.get("id");
    if (id) {
      this._mode = "edit";
      this._clientId = id;
      return;
    }
    this._mode = "create";
    this._clientId = null;
  }

  _wireFor(mode) {
    if (mode === "create") {
      this._setupCreateMode();
    } else {
      this._setupEditMode();
    }
  }

  // ===========================================================================
  // CREATE MODE
  // ===========================================================================

  _setupCreateMode() {
    if (this.hasOverlineTarget) this.overlineTarget.textContent = "Nouveau client";
    if (this.hasTitleTarget) this.titleTarget.textContent = "Nouveau client";
    if (this.hasCrumbTarget) this.crumbTarget.textContent = "Nouveau";
    if (this.hasModeLabelTarget) this.modeLabelTarget.textContent = "création";

    // En création, les 2 blocs sont en édition directe (pas de bouton
    // « Modifier ») et le bouton global « Créer ce client » remplace
    // les boutons « Enregistrer » par bloc.
    this._setBlockEditingNoCancel(this.identityBlockTarget,
      this.identityOpenBtnTarget, this.identityCancelBtnTarget, this.identitySubmitBtnTarget);
    this._setBlockEditingNoCancel(this.billingBlockTarget,
      this.billingOpenBtnTarget, this.billingCancelBtnTarget, this.billingSubmitBtnTarget);

    if (this.hasCreateActionsTarget) this.createActionsTarget.hidden = false;

    // Default type=b2c — Particulier (déjà la valeur sélectionnée dans le
    // markup). L'utilisateur passe à Entreprise/Administration soit
    // manuellement, soit via le bloc « Remplissage automatique » qui déduit
    // le type depuis nature_juridique.
    this._applyTypeVisibility(this._readType());
  }

  _setBlockEditingNoCancel(block, openBtn, cancelBtn, submitBtn) {
    if (!block) return;
    block.dataset.editState = "editing";
    const summary = block.querySelector("[data-edit-summary]");
    const form = block.querySelector("[data-edit-form]");
    if (summary) summary.hidden = true;
    if (form) form.hidden = false;
    if (openBtn) openBtn.hidden = true;
    if (cancelBtn) cancelBtn.hidden = true;
    if (submitBtn) submitBtn.hidden = true; // remplacé par le bouton global
  }

  async submitCreate(event) {
    event?.preventDefault?.();
    this._clearAllErrors();
    if (this.hasCreateErrorTarget) this.createErrorTarget.hidden = true;

    const type = this._readType();
    const legalName = this._read(this.legalNameInputTarget);
    const siret = this._read(this.siretInputTarget);
    const vatIntracom = this._read(this.vatInputTarget);
    const primaryEmail = this._read(this.primaryEmailInputTarget);
    const primaryPhone = this._read(this.primaryPhoneInputTarget);

    // Validation client-side minimale (les règles métier complètes restent côté backend).
    if (!legalName) {
      this._fieldError("legalName", "La raison sociale est obligatoire.");
      this.legalNameInputTarget.focus();
      return;
    }
    if ((type === "b2b" || type === "public_administration") && !siret) {
      this._fieldError("siret", "Le SIRET est obligatoire pour ce type de client.");
      this.siretInputTarget.focus();
      return;
    }
    if ((type === "b2b" || type === "public_administration") && siret && !this._isLuhnValidSiret(siret)) {
      this._fieldError("siret", "Le SIRET doit faire 14 chiffres et passer le contrôle Luhn.");
      this.siretInputTarget.focus();
      return;
    }
    if (type === "b2c" && !primaryEmail) {
      this._fieldError("primaryEmail", "L'email du client est obligatoire pour un particulier.");
      // L'input email est dans le bloc Identité côté création — pas affiché dans le DOM
      // actuel ; la saisie de l'email du contact principal en création se fait via le
      // champ « primary-email » du bloc contact principal, masqué en création. On
      // matérialise donc la saisie comme une variante du bloc Identité (TODO 9.5.1 si
      // besoin d'ajouter un champ visible en création b2c). Pour V1 : on retombe sur
      // un message d'erreur du bouton global.
    }

    // Adresse facturation (obligatoire pour EN16931 dès la création).
    const billing = {
      line1: this._read(this.billingLine1InputTarget),
      line2: this._read(this.billingLine2InputTarget) || null,
      postalCode: this._read(this.billingPostalInputTarget),
      city: this._read(this.billingCityInputTarget),
      country: this._read(this.billingCountryInputTarget) || "FR",
    };
    if (!billing.line1) {
      this._fieldError("billingLine1", "L'adresse est obligatoire.");
      this.billingLine1InputTarget.focus();
      return;
    }
    if (!/^\d{5}$/.test(billing.postalCode || "")) {
      this._fieldError("billingPostal", "Code postal à 5 chiffres attendu.");
      this.billingPostalInputTarget.focus();
      return;
    }
    if (!billing.city) {
      this._fieldError("billingCity", "La ville est obligatoire.");
      this.billingCityInputTarget.focus();
      return;
    }

    this._setBusy(this.createSubmitBtnTarget, true);
    try {
      // 1. Core client
      const corePayload = { type, legalName };
      if (siret) corePayload.siret = siret;
      if (vatIntracom) corePayload.vatIntracom = vatIntracom;

      let created;
      try {
        created = await api.createClient(corePayload);
      } catch (error) {
        this._handleApiError(error, "Impossible de créer le client.");
        return;
      }

      const newId = created?.id;
      if (!newId) {
        this._showCreateError("Réponse serveur invalide (id manquant).");
        return;
      }
      this._clientId = newId;

      // 2. Adresse facturation
      try {
        await api.addClientAddress(newId, {
          type: "billing",
          line1: billing.line1,
          line2: billing.line2,
          postalCode: billing.postalCode,
          city: billing.city,
          country: billing.country,
          isDefault: true,
        });
      } catch (error) {
        // Le client a été créé. On bascule en édition pour permettre la correction.
        this._handleApiError(error, "Adresse de facturation invalide. Corrigez puis enregistrez ce bloc.", "billing");
        this._redirectToEdit(newId);
        return;
      }

      // 3. Contact primaire (b2c → toujours créé si email fourni)
      if (primaryEmail) {
        try {
          await api.addClientContact(newId, {
            email: primaryEmail,
            phone: primaryPhone || null,
            isPrimary: true,
          });
        } catch (error) {
          this._handleApiError(error, "Le contact principal n'a pas pu être créé. Corrigez puis enregistrez le bloc « Contact principal ».", "primary");
          this._redirectToEdit(newId);
          return;
        }
      }

      this.showSuccess("Client créé.");
      this._redirectToEdit(newId);
    } finally {
      this._setBusy(this.createSubmitBtnTarget, false);
    }
  }

  _redirectToEdit(id) {
    window.location.assign(`/clients-edition.html?id=${encodeURIComponent(id)}`);
  }

  _showCreateError(msg) {
    if (this.hasCreateErrorTarget) {
      this.createErrorTarget.textContent = msg;
      this.createErrorTarget.hidden = false;
    } else {
      this.showError(msg);
    }
  }

  // ===========================================================================
  // EDIT MODE
  // ===========================================================================

  async _setupEditMode() {
    if (this.hasOverlineTarget) this.overlineTarget.textContent = "Modification client";
    if (this.hasModeLabelTarget) this.modeLabelTarget.textContent = "édition par bloc";
    if (this.hasCreateActionsTarget) this.createActionsTarget.hidden = true;

    // Le bloc global « Remplissage automatique » n'existe qu'en création :
    // en édition, chaque bloc expose son propre bouton « Récupérer… » ciblé
    // (cf. 99.58), évitant l'écrasement multi-blocs d'un seul geste.
    if (this.hasAutofillBlockTarget) this.autofillBlockTarget.hidden = true;

    // Tous les blocs sont visibles en édition.
    if (this.hasShippingBlockTarget) this.shippingBlockTarget.hidden = false;
    if (this.hasPrimaryBlockTarget) this.primaryBlockTarget.hidden = false;
    if (this.hasContactsListTarget) this.contactsListTarget.hidden = false;
    if (this.hasNewContactBlockTarget) this.newContactBlockTarget.hidden = false;

    // En édition, les blocs Identité + Facturation démarrent en mode résumé.
    this._setBlockSummary(this.identityBlockTarget);
    this._setBlockSummary(this.billingBlockTarget);
    this._setBlockSummary(this.shippingBlockTarget);
    this._setBlockSummary(this.primaryBlockTarget);
    this._setBlockSummary(this.newContactBlockTarget);

    // Restaurer la visibilité Annuler + Submit par bloc.
    [this.identityCancelBtnTarget, this.billingCancelBtnTarget].forEach((b) => { if (b) b.hidden = false; });
    [this.identitySubmitBtnTarget, this.billingSubmitBtnTarget].forEach((b) => { if (b) b.hidden = false; });

    try {
      const client = await api.getClient(this._clientId);
      this._client = client;
      this._hydrate(client);
    } catch (error) {
      const msg = error instanceof APIError && error.status === 404
        ? "Client introuvable."
        : (error && error.message) || "Impossible de charger le client.";
      if (this.hasLoadErrorTarget) {
        this.loadErrorTarget.textContent = msg;
        this.loadErrorTarget.hidden = false;
      } else {
        this.showError(msg);
      }
    }
  }

  _hydrate(client) {
    const legalName = client.legalName || "—";
    if (this.hasTitleTarget) this.titleTarget.textContent = legalName;
    if (this.hasCrumbClientTarget) {
      this.crumbClientTarget.hidden = false;
      const link = document.createElement("a");
      link.setAttribute("href", `/clients-detail.html?id=${encodeURIComponent(client.id)}`);
      link.textContent = legalName;
      this.crumbClientTarget.replaceChildren(link);
    }
    if (this.hasCrumbSepTarget) this.crumbSepTarget.hidden = false;
    if (this.hasCrumbTarget) this.crumbTarget.textContent = "Édition";
    document.title = `Notamicro — ${legalName} (édition)`;

    // Identité
    if (this.hasTypeInputTarget) {
      this.typeInputTarget.value = client.type ?? "b2c";
      // Type immuable en édition (cf. décision plan).
      this.typeInputTarget.disabled = true;
    }
    if (this.hasLegalNameInputTarget) this.legalNameInputTarget.value = client.legalName ?? "";
    if (this.hasSiretInputTarget) this.siretInputTarget.value = client.siret ?? "";
    if (this.hasVatInputTarget) this.vatInputTarget.value = client.vatIntracom ?? "";
    this._applyTypeVisibility(client.type);
    this._renderIdentitySummary(client);

    // Adresses
    const addresses = Array.isArray(client.addresses) ? client.addresses : [];
    const activeAddresses = addresses.filter((a) => a.status !== "archived");
    this._billing = activeAddresses.find((a) => a.type === "billing") || null;
    this._shipping = activeAddresses.find((a) => a.type === "shipping") || null;
    this._hydrateBilling(this._billing);
    this._hydrateShipping(this._shipping);

    // Contacts
    const contacts = Array.isArray(client.contacts) ? client.contacts : [];
    const activeContacts = contacts.filter((c) => c.status !== "archived");
    this._primary = activeContacts.find((c) => c.isPrimary === true) || null;
    this._additional = activeContacts.filter((c) => c.isPrimary !== true);
    this._hydratePrimary(this._primary, client.type);
    this._renderAdditionalContacts(this._additional);
  }

  // ---------------------------------------------------------------------------
  // Bloc Identité — open / cancel / submit
  // ---------------------------------------------------------------------------

  openIdentity(event) {
    event?.preventDefault?.();
    this._openBlock(this.identityBlockTarget);
  }

  cancelIdentity(event) {
    event?.preventDefault?.();
    this._clearIdentityErrors();
    if (this._client) {
      if (this.hasLegalNameInputTarget) this.legalNameInputTarget.value = this._client.legalName ?? "";
      if (this.hasSiretInputTarget) this.siretInputTarget.value = this._client.siret ?? "";
      if (this.hasVatInputTarget) this.vatInputTarget.value = this._client.vatIntracom ?? "";
    }
    this._closeBlock(this.identityBlockTarget);
  }

  async submitIdentity(event) {
    event?.preventDefault?.();
    event?.stopImmediatePropagation?.();
    if (this._mode === "create") {
      // Sous-bloc « Enregistrer » d'Identité ne fait rien en création — le bouton
      // global « Créer ce client » orchestre.
      return;
    }
    this._clearIdentityErrors();
    const legalName = this._read(this.legalNameInputTarget);
    const siret = this._read(this.siretInputTarget);
    const vatIntracom = this._read(this.vatInputTarget);
    const type = this._client?.type;

    if (!legalName) {
      this._fieldError("legalName", "La raison sociale est obligatoire.");
      return;
    }
    if ((type === "b2b" || type === "public_administration") && siret && !this._isLuhnValidSiret(siret)) {
      this._fieldError("siret", "Le SIRET doit faire 14 chiffres et passer le contrôle Luhn.");
      return;
    }

    const payload = { legalName };
    if (siret !== this._client?.siret) payload.siret = siret || null;
    if (vatIntracom !== this._client?.vatIntracom) payload.vatIntracom = vatIntracom || null;

    this._setBusy(this.identitySubmitBtnTarget, true);
    try {
      const updated = await api.updateClient(this._clientId, payload);
      this._client = { ...this._client, ...updated };
      this._renderIdentitySummary(this._client);
      this._closeBlock(this.identityBlockTarget);
      this.showSuccess("Identité enregistrée.");
    } catch (error) {
      this._handleApiError(error, "Modification de l'identité refusée.");
    } finally {
      this._setBusy(this.identitySubmitBtnTarget, false);
    }
  }

  _clearIdentityErrors() {
    this._hideError(this.legalNameErrorTarget);
    this._hideError(this.siretErrorTarget);
    this._hideError(this.vatErrorTarget);
    this._hideError(this.typeErrorTarget);
    this._clearAriaInvalid([
      this.legalNameInputTarget, this.siretInputTarget,
      this.vatInputTarget, this.typeInputTarget,
    ]);
  }

  _renderIdentitySummary(client) {
    if (!this.hasIdentitySummaryTarget) return;
    const lines = [];
    lines.push(`<strong>${this._escape(client.legalName ?? "—")}</strong> · ${this._escape(TYPE_LABELS_FR[client.type] ?? "—")}`);
    const ids = [];
    if (client.siret) ids.push(`<span class="is-mono">SIRET ${this._escape(client.siret)}</span>`);
    else if (client.siren) ids.push(`<span class="is-mono">SIREN ${this._escape(client.siren)}</span>`);
    if (client.vatIntracom) ids.push(`<span class="is-mono">TVA ${this._escape(client.vatIntracom)}</span>`);
    if (ids.length > 0) lines.push(ids.join(" · "));
    this.identitySummaryTarget.innerHTML = lines.join("<br/>");
  }

  // ---------------------------------------------------------------------------
  // Bloc Adresse facturation
  // ---------------------------------------------------------------------------

  openBilling(event) {
    event?.preventDefault?.();
    this._openBlock(this.billingBlockTarget);
  }

  cancelBilling(event) {
    event?.preventDefault?.();
    this._clearBillingErrors();
    this._hydrateBilling(this._billing);
    this._closeBlock(this.billingBlockTarget);
  }

  async submitBilling(event) {
    event?.preventDefault?.();
    event?.stopImmediatePropagation?.();
    if (this._mode === "create") return;
    this._clearBillingErrors();
    const payload = this._readBillingPayload();
    if (!payload) return;

    this._setBusy(this.billingSubmitBtnTarget, true);
    try {
      if (this._billing && this._billing.id) {
        const updated = await api.updateClientAddress(this._clientId, this._billing.id, payload);
        this._billing = updated;
      } else {
        const created = await api.addClientAddress(this._clientId, {
          ...payload,
          type: "billing",
          isDefault: true,
        });
        this._billing = created;
      }
      this._hydrateBilling(this._billing);
      this._closeBlock(this.billingBlockTarget);
      this.showSuccess("Adresse de facturation enregistrée.");
    } catch (error) {
      this._handleApiError(error, "Adresse de facturation refusée.", "billing");
    } finally {
      this._setBusy(this.billingSubmitBtnTarget, false);
    }
  }

  _readBillingPayload() {
    const line1 = this._read(this.billingLine1InputTarget);
    const postalCode = this._read(this.billingPostalInputTarget);
    const city = this._read(this.billingCityInputTarget);
    const country = this._read(this.billingCountryInputTarget) || "FR";

    if (!line1) {
      this._fieldError("billingLine1", "L'adresse est obligatoire.");
      return null;
    }
    if (!/^\d{5}$/.test(postalCode || "")) {
      this._fieldError("billingPostal", "Code postal à 5 chiffres attendu.");
      return null;
    }
    if (!city) {
      this._fieldError("billingCity", "La ville est obligatoire.");
      return null;
    }
    return {
      line1,
      line2: this._read(this.billingLine2InputTarget) || null,
      postalCode,
      city,
      country,
    };
  }

  _clearBillingErrors() {
    this._hideError(this.billingLine1ErrorTarget);
    this._hideError(this.billingPostalErrorTarget);
    this._hideError(this.billingCityErrorTarget);
    this._clearAriaInvalid([
      this.billingLine1InputTarget, this.billingPostalInputTarget,
      this.billingCityInputTarget, this.billingCountryInputTarget,
    ]);
  }

  _hydrateBilling(address) {
    if (this.hasBillingLine1InputTarget) this.billingLine1InputTarget.value = address?.line1 ?? "";
    if (this.hasBillingLine2InputTarget) this.billingLine2InputTarget.value = address?.line2 ?? "";
    if (this.hasBillingPostalInputTarget) this.billingPostalInputTarget.value = address?.postalCode ?? "";
    if (this.hasBillingCityInputTarget) this.billingCityInputTarget.value = address?.city ?? "";
    if (this.hasBillingCountryInputTarget) this.billingCountryInputTarget.value = address?.country ?? "FR";
    this._renderBillingSummary(address);
  }

  _renderBillingSummary(address) {
    if (!this.hasBillingSummaryTarget) return;
    if (!address) {
      this.billingSummaryTarget.innerHTML = `<span class="is-muted">Aucune adresse de facturation.</span>`;
      return;
    }
    const cityLine = `${this._escape(address.postalCode || "")} ${this._escape(address.city || "")}`;
    const country = COUNTRY_LABELS_FR[address.country] || address.country || "France";
    const parts = [this._escape(address.line1 || "")];
    if (address.line2) parts.push(this._escape(address.line2));
    parts.push(`${cityLine}, ${this._escape(country)}`);
    this.billingSummaryTarget.innerHTML = parts.join("<br/>");
  }

  // ---------------------------------------------------------------------------
  // Bloc Adresse livraison
  // ---------------------------------------------------------------------------

  openShipping(event) {
    event?.preventDefault?.();
    this._openBlock(this.shippingBlockTarget);
  }

  cancelShipping(event) {
    event?.preventDefault?.();
    this._clearShippingErrors();
    this._hydrateShipping(this._shipping);
    this._closeBlock(this.shippingBlockTarget);
  }

  onShippingSameChange() {
    if (!this.hasShippingFieldsTarget) return;
    const same = this.hasShippingSameInputTarget && this.shippingSameInputTarget.checked;
    this.shippingFieldsTarget.hidden = same;
  }

  async submitShipping(event) {
    event?.preventDefault?.();
    event?.stopImmediatePropagation?.();
    this._clearShippingErrors();

    const same = this.hasShippingSameInputTarget && this.shippingSameInputTarget.checked;

    this._setBusy(this.shippingSubmitBtnTarget, true);
    try {
      if (same) {
        // L'utilisateur veut « identique » : archiver toute adresse shipping existante.
        if (this._shipping && this._shipping.id) {
          await api.archiveClientAddress(this._clientId, this._shipping.id);
          this._shipping = null;
        }
      } else {
        const payload = this._readShippingPayload();
        if (!payload) {
          this._setBusy(this.shippingSubmitBtnTarget, false);
          return;
        }
        if (this._shipping && this._shipping.id) {
          this._shipping = await api.updateClientAddress(this._clientId, this._shipping.id, payload);
        } else {
          this._shipping = await api.addClientAddress(this._clientId, {
            ...payload,
            type: "shipping",
            isDefault: true,
          });
        }
      }
      this._hydrateShipping(this._shipping);
      this._closeBlock(this.shippingBlockTarget);
      this.showSuccess("Adresse de livraison enregistrée.");
    } catch (error) {
      this._handleApiError(error, "Adresse de livraison refusée.", "shipping");
    } finally {
      this._setBusy(this.shippingSubmitBtnTarget, false);
    }
  }

  _readShippingPayload() {
    const line1 = this._read(this.shippingLine1InputTarget);
    const postalCode = this._read(this.shippingPostalInputTarget);
    const city = this._read(this.shippingCityInputTarget);
    const country = this._read(this.shippingCountryInputTarget) || "FR";

    if (!line1) {
      this._fieldError("shippingLine1", "L'adresse est obligatoire.");
      return null;
    }
    if (!/^\d{5}$/.test(postalCode || "")) {
      this._fieldError("shippingPostal", "Code postal à 5 chiffres attendu.");
      return null;
    }
    if (!city) {
      this._fieldError("shippingCity", "La ville est obligatoire.");
      return null;
    }
    return {
      line1,
      line2: this._read(this.shippingLine2InputTarget) || null,
      postalCode,
      city,
      country,
    };
  }

  _clearShippingErrors() {
    this._hideError(this.shippingLine1ErrorTarget);
    this._hideError(this.shippingPostalErrorTarget);
    this._hideError(this.shippingCityErrorTarget);
  }

  _hydrateShipping(address) {
    const hasShipping = !!address;
    if (this.hasShippingSameInputTarget) {
      this.shippingSameInputTarget.checked = !hasShipping;
    }
    if (this.hasShippingFieldsTarget) {
      this.shippingFieldsTarget.hidden = !hasShipping;
    }
    if (this.hasShippingLine1InputTarget) this.shippingLine1InputTarget.value = address?.line1 ?? "";
    if (this.hasShippingLine2InputTarget) this.shippingLine2InputTarget.value = address?.line2 ?? "";
    if (this.hasShippingPostalInputTarget) this.shippingPostalInputTarget.value = address?.postalCode ?? "";
    if (this.hasShippingCityInputTarget) this.shippingCityInputTarget.value = address?.city ?? "";
    if (this.hasShippingCountryInputTarget) this.shippingCountryInputTarget.value = address?.country ?? "FR";
    this._renderShippingSummary(address);
  }

  _renderShippingSummary(address) {
    if (!this.hasShippingSummaryTarget) return;
    if (!address) {
      this.shippingSummaryTarget.innerHTML = `<span class="text-muted">Identique à l'adresse de facturation.</span>`;
      return;
    }
    const cityLine = `${this._escape(address.postalCode || "")} ${this._escape(address.city || "")}`;
    const country = COUNTRY_LABELS_FR[address.country] || address.country || "France";
    const parts = [this._escape(address.line1 || "")];
    if (address.line2) parts.push(this._escape(address.line2));
    parts.push(`${cityLine}, ${this._escape(country)}`);
    this.shippingSummaryTarget.innerHTML = parts.join("<br/>");
  }

  // ---------------------------------------------------------------------------
  // Bloc Contact principal
  // ---------------------------------------------------------------------------

  openPrimary(event) {
    event?.preventDefault?.();
    this._openBlock(this.primaryBlockTarget);
  }

  cancelPrimary(event) {
    event?.preventDefault?.();
    this._hideError(this.primaryEmailErrorTarget);
    this._hydratePrimary(this._primary, this._client?.type);
    this._closeBlock(this.primaryBlockTarget);
  }

  async submitPrimary(event) {
    event?.preventDefault?.();
    event?.stopImmediatePropagation?.();
    this._hideError(this.primaryEmailErrorTarget);
    const email = this._read(this.primaryEmailInputTarget);
    const phone = this._read(this.primaryPhoneInputTarget);
    const type = this._client?.type;

    if (type === "b2c" && !email) {
      this._fieldError("primaryEmail", "L'email est obligatoire pour un particulier.");
      return;
    }
    if (!this._primary && !email) {
      this._fieldError("primaryEmail", "Renseignez un email pour créer un contact principal.");
      return;
    }

    this._setBusy(this.primarySubmitBtnTarget, true);
    try {
      if (this._primary && this._primary.id) {
        const payload = { email, phone: phone || null };
        this._primary = await api.updateClientContact(this._clientId, this._primary.id, payload);
      } else {
        this._primary = await api.addClientContact(this._clientId, {
          email,
          phone: phone || null,
          isPrimary: true,
        });
      }
      this._hydratePrimary(this._primary, type);
      this._closeBlock(this.primaryBlockTarget);
      this.showSuccess("Contact principal enregistré.");
    } catch (error) {
      this._handleApiError(error, "Contact principal refusé.", "primary");
    } finally {
      this._setBusy(this.primarySubmitBtnTarget, false);
    }
  }

  _hydratePrimary(contact, type) {
    if (this.hasPrimaryEmailInputTarget) this.primaryEmailInputTarget.value = contact?.email ?? "";
    if (this.hasPrimaryPhoneInputTarget) this.primaryPhoneInputTarget.value = contact?.phone ?? "";
    if (this.hasPrimaryEmailStarTarget) {
      this.primaryEmailStarTarget.hidden = type !== "b2c";
    }
    if (this.hasPrimaryEmailInputTarget) {
      if (type === "b2c") {
        this.primaryEmailInputTarget.setAttribute("required", "");
        this.primaryEmailInputTarget.setAttribute("aria-required", "true");
      } else {
        this.primaryEmailInputTarget.removeAttribute("required");
        this.primaryEmailInputTarget.removeAttribute("aria-required");
      }
    }
    this._renderPrimarySummary(contact);
  }

  _renderPrimarySummary(contact) {
    if (!this.hasPrimarySummaryTarget) return;
    if (!contact) {
      this.primarySummaryTarget.innerHTML = `<span class="is-muted">Aucun contact principal défini.</span>`;
      return;
    }
    const email = this._escape(contact.email || "—");
    const phone = contact.phone ? `<br/><span class="text-muted">${this._escape(contact.phone)}</span>` : "";
    this.primarySummaryTarget.innerHTML = `<a href="mailto:${email}" style="text-decoration: underline;">${email}</a>${phone}`;
  }

  // ---------------------------------------------------------------------------
  // Contacts additionnels (liste + bloc « ajouter »)
  // ---------------------------------------------------------------------------

  _renderAdditionalContacts(contacts) {
    if (!this.hasContactsListTarget) return;
    while (this.contactsListTarget.firstChild) {
      this.contactsListTarget.removeChild(this.contactsListTarget.firstChild);
    }
    for (const c of contacts) {
      this.contactsListTarget.appendChild(this._buildContactBlock(c));
    }
  }

  _buildContactBlock(contact) {
    const fragment = this.cloneTemplate("tpl-contact-block");
    const article = fragment.querySelector("[data-contact-block]");
    if (!article) return fragment;
    article.id = `block-contact-${contact.id}`;

    const title = fragment.querySelector('[data-slot="blockTitle"]');
    const summary = fragment.querySelector("[data-contact-summary]");
    const form = fragment.querySelector("[data-contact-form]");
    const openBtn = fragment.querySelector("[data-contact-open]");
    const cancelBtn = fragment.querySelector("[data-contact-cancel]");
    const deleteBtn = fragment.querySelector("[data-contact-delete]");
    const submitBtn = fragment.querySelector("[data-contact-submit]");
    const firstName = fragment.querySelector("[data-contact-firstname]");
    const lastName = fragment.querySelector("[data-contact-lastname]");
    const fnRole = fragment.querySelector("[data-contact-function]");
    const email = fragment.querySelector("[data-contact-email]");
    const errorEl = fragment.querySelector("[data-contact-error]");

    // Câblage des `for` / `id` uniques par contact (accessibilité labels).
    const uid = `c-${contact.id}`;
    if (firstName) firstName.id = `${uid}-firstName`;
    if (lastName) lastName.id = `${uid}-lastName`;
    if (fnRole) fnRole.id = `${uid}-function`;
    if (email) email.id = `${uid}-email`;
    const labels = {
      firstname: fragment.querySelector("[data-contact-firstname-label]"),
      lastname: fragment.querySelector("[data-contact-lastname-label]"),
      func: fragment.querySelector("[data-contact-function-label]"),
      email: fragment.querySelector("[data-contact-email-label]"),
    };
    if (labels.firstname) labels.firstname.setAttribute("for", `${uid}-firstName`);
    if (labels.lastname) labels.lastname.setAttribute("for", `${uid}-lastName`);
    if (labels.func) labels.func.setAttribute("for", `${uid}-function`);
    if (labels.email) labels.email.setAttribute("for", `${uid}-email`);

    const fullName = [contact.firstName, contact.lastName].filter(Boolean).join(" ") || contact.email;
    if (title) title.textContent = contact.function ? `${fullName} — ${contact.function}` : fullName;

    if (summary) {
      summary.innerHTML = `<a href="mailto:${this._escape(contact.email)}" style="text-decoration: underline;">${this._escape(contact.email)}</a>`;
    }

    if (firstName) firstName.value = contact.firstName ?? "";
    if (lastName) lastName.value = contact.lastName ?? "";
    if (fnRole) fnRole.value = contact.function ?? "";
    if (email) email.value = contact.email ?? "";

    if (openBtn) {
      openBtn.setAttribute("aria-label", `Modifier le contact ${fullName}`);
      openBtn.addEventListener("click", (ev) => {
        ev.preventDefault();
        this._openBlock(article);
      });
    }
    if (cancelBtn) {
      cancelBtn.addEventListener("click", (ev) => {
        ev.preventDefault();
        if (errorEl) errorEl.hidden = true;
        if (firstName) firstName.value = contact.firstName ?? "";
        if (lastName) lastName.value = contact.lastName ?? "";
        if (fnRole) fnRole.value = contact.function ?? "";
        if (email) email.value = contact.email ?? "";
        this._closeBlock(article);
      });
    }
    if (deleteBtn) {
      deleteBtn.setAttribute("aria-label", `Supprimer le contact ${fullName}`);
      deleteBtn.addEventListener("click", async (ev) => {
        ev.preventDefault();
        if (!window.confirm(`Supprimer le contact ${fullName} ?`)) return;
        deleteBtn.disabled = true;
        try {
          await api.archiveClientContact(this._clientId, contact.id);
          // Retire le bloc de la liste + de l'état interne.
          article.remove();
          this._additional = this._additional.filter((c) => c.id !== contact.id);
          this.showSuccess("Contact supprimé.");
        } catch (error) {
          const msg = (error && error.message) || "Suppression refusée.";
          if (errorEl) {
            errorEl.textContent = msg;
            errorEl.hidden = false;
          }
          deleteBtn.disabled = false;
        }
      });
    }
    if (form) {
      form.addEventListener("submit", async (ev) => {
        ev.preventDefault();
        ev.stopImmediatePropagation();
        if (errorEl) errorEl.hidden = true;
        const newEmail = email?.value?.trim();
        if (!newEmail) {
          if (errorEl) {
            errorEl.textContent = "L'email est obligatoire.";
            errorEl.hidden = false;
          }
          email?.focus();
          return;
        }
        if (submitBtn) submitBtn.disabled = true;
        try {
          const payload = {
            email: newEmail,
            firstName: firstName?.value?.trim() || null,
            lastName: lastName?.value?.trim() || null,
            function: fnRole?.value?.trim() || null,
          };
          const updated = await api.updateClientContact(this._clientId, contact.id, payload);
          // Met à jour l'état + le titre du bloc.
          const idx = this._additional.findIndex((c) => c.id === contact.id);
          if (idx >= 0) this._additional[idx] = updated;
          const newFull = [updated.firstName, updated.lastName].filter(Boolean).join(" ") || updated.email;
          if (title) title.textContent = updated.function ? `${newFull} — ${updated.function}` : newFull;
          if (summary) {
            summary.innerHTML = `<a href="mailto:${this._escape(updated.email)}" style="text-decoration: underline;">${this._escape(updated.email)}</a>`;
          }
          this._closeBlock(article);
          this.showSuccess("Contact enregistré.");
        } catch (error) {
          const msg = this._extractMessage(error) || "Contact refusé.";
          if (errorEl) {
            errorEl.textContent = msg;
            errorEl.hidden = false;
          }
        } finally {
          if (submitBtn) submitBtn.disabled = false;
        }
      });
    }

    return fragment;
  }

  openNewContact(event) {
    event?.preventDefault?.();
    if (this.hasNewContactEmailErrorTarget) this.newContactEmailErrorTarget.hidden = true;
    if (this.hasNewContactFirstNameInputTarget) this.newContactFirstNameInputTarget.value = "";
    if (this.hasNewContactLastNameInputTarget) this.newContactLastNameInputTarget.value = "";
    if (this.hasNewContactFunctionInputTarget) this.newContactFunctionInputTarget.value = "";
    if (this.hasNewContactEmailInputTarget) this.newContactEmailInputTarget.value = "";
    this._openBlock(this.newContactBlockTarget);
  }

  cancelNewContact(event) {
    event?.preventDefault?.();
    if (this.hasNewContactEmailErrorTarget) this.newContactEmailErrorTarget.hidden = true;
    this._closeBlock(this.newContactBlockTarget);
  }

  async submitNewContact(event) {
    event?.preventDefault?.();
    event?.stopImmediatePropagation?.();
    if (this.hasNewContactEmailErrorTarget) this.newContactEmailErrorTarget.hidden = true;
    const email = this._read(this.newContactEmailInputTarget);
    if (!email) {
      this._fieldError("newContactEmail", "L'email est obligatoire.");
      this.newContactEmailInputTarget.focus();
      return;
    }
    this._setBusy(this.newContactSubmitBtnTarget, true);
    try {
      const created = await api.addClientContact(this._clientId, {
        email,
        firstName: this._read(this.newContactFirstNameInputTarget) || null,
        lastName: this._read(this.newContactLastNameInputTarget) || null,
        function: this._read(this.newContactFunctionInputTarget) || null,
        isPrimary: false,
      });
      this._additional.push(created);
      if (this.hasContactsListTarget) {
        this.contactsListTarget.appendChild(this._buildContactBlock(created));
      }
      this._closeBlock(this.newContactBlockTarget);
      this.showSuccess("Contact ajouté.");
    } catch (error) {
      this._handleApiError(error, "Ajout du contact refusé.", "newContact");
    } finally {
      this._setBusy(this.newContactSubmitBtnTarget, false);
    }
  }

  // ---------------------------------------------------------------------------
  // Remplissage automatique (recherche-entreprises.api.gouv.fr)
  // ---------------------------------------------------------------------------

  preventAutofillSubmit(event) { event.preventDefault(); }

  onAutofillInput() {
    if (!this.hasAutofillInputTarget) return;
    const digits = this._siretDigits(this.autofillInputTarget.value);
    if (digits.length < 9) {
      this._hideAutofillSuggestions();
      return;
    }
    clearTimeout(this._autofillDebounce);
    this._autofillDebounce = setTimeout(() => {
      this._searchAutofillSuggestions(digits).catch(() => {
        // Le réseau peut être indisponible (offline, rate-limit 429). On
        // n'affiche pas d'erreur intrusive — l'utilisateur peut toujours
        // saisir les champs manuellement dans Identité.
        this._hideAutofillSuggestions();
      });
    }, 250);
  }

  onAutofillFocus() {
    if (this._lastAutofillResults && this._lastAutofillResults.length > 0) {
      this._renderAutofillSuggestions(this._lastAutofillResults);
    }
  }

  onAutofillBlur() {
    // Petit délai pour laisser le mousedown sur un item traverser avant la fermeture.
    setTimeout(() => this._hideAutofillSuggestions(), 120);
  }

  onAutofillKeydown(event) {
    if (event.key === "Escape") {
      this._hideAutofillSuggestions();
      return;
    }
    if (!this.hasAutofillSuggestListTarget || this.autofillSuggestListTarget.hidden) return;
    const items = Array.from(this.autofillSuggestListTarget.querySelectorAll('[role="option"]'));
    if (items.length === 0) return;
    const current = items.findIndex((el) => el.getAttribute("aria-selected") === "true");
    if (event.key === "ArrowDown") {
      event.preventDefault();
      this._highlightSuggestion(items, (current + 1) % items.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      this._highlightSuggestion(items, (current - 1 + items.length) % items.length);
    } else if (event.key === "Enter" && current >= 0) {
      event.preventDefault();
      items[current].click();
    }
  }

  async _searchAutofillSuggestions(digits) {
    const results = await searchEntreprises(digits, { include: "siege" });
    this._lastAutofillResults = results;
    this._renderAutofillSuggestions(results);
  }

  _renderAutofillSuggestions(results) {
    if (!this.hasAutofillSuggestListTarget) return;
    const list = this.autofillSuggestListTarget;
    while (list.firstChild) list.removeChild(list.firstChild);
    if (results.length === 0) {
      this._hideAutofillSuggestions();
      return;
    }
    for (const result of results) {
      const siege = result?.siege || {};
      const siret = siege.siret || "";
      const name = result?.nom_raison_sociale || result?.nom_complet || "—";
      const address = siege.adresse || "";
      const li = document.createElement("li");
      li.setAttribute("role", "option");
      li.setAttribute("aria-selected", "false");
      li.style.cssText = "padding: 8px 12px; cursor: pointer;";
      const strong = document.createElement("strong");
      strong.textContent = name;
      const meta = document.createElement("span");
      meta.className = "fs-13 text-muted";
      meta.style.display = "block";
      meta.textContent = address ? `${address} · SIRET ${this._formatSiret(siret)}` : `SIRET ${this._formatSiret(siret)}`;
      li.appendChild(strong);
      li.appendChild(meta);
      // mousedown plutôt que click pour battre le blur de l'input.
      li.addEventListener("mousedown", (ev) => {
        ev.preventDefault();
        this._selectAutofillSuggestion(result);
      });
      li.addEventListener("mouseenter", () => {
        const items = Array.from(list.querySelectorAll('[role="option"]'));
        this._highlightSuggestion(items, items.indexOf(li));
      });
      list.appendChild(li);
    }
    list.hidden = false;
    if (this.hasAutofillInputTarget) this.autofillInputTarget.setAttribute("aria-expanded", "true");
  }

  _highlightSuggestion(items, index) {
    items.forEach((el, i) => {
      const active = i === index;
      el.setAttribute("aria-selected", active ? "true" : "false");
      el.style.background = active ? "var(--surface-2, rgba(0,0,0,0.04))" : "";
    });
  }

  _hideAutofillSuggestions() {
    if (!this.hasAutofillSuggestListTarget) return;
    this.autofillSuggestListTarget.hidden = true;
    if (this.hasAutofillInputTarget) this.autofillInputTarget.setAttribute("aria-expanded", "false");
  }

  _selectAutofillSuggestion(result) {
    const siege = result?.siege || {};
    const siret = siege.siret || "";
    const siren = result?.siren || (siret ? siret.slice(0, 9) : "");
    const name = result?.nom_raison_sociale || result?.nom_complet || "";

    // Déduction du type depuis la catégorie juridique INSEE (nature_juridique).
    // Codes commençant par "7" = personnes morales soumises au droit
    // administratif (communes, EPCI, départements, régions, État,
    // établissements publics administratifs). Tout autre code (1=EI, 5=société,
    // 6=RCS, 8=organisme privé, 9=groupement) → b2b.
    const natureCode = String(result?.nature_juridique || "");
    const deducedType = natureCode.startsWith("7") ? "public_administration" : "b2b";

    if (this.hasTypeInputTarget && !this.typeInputTarget.disabled) {
      this.typeInputTarget.value = deducedType;
      this._applyTypeVisibility(deducedType);
    }

    // Identité : SIRET et raison sociale sont écrasés (intention explicite de
    // l'utilisateur en sélectionnant une entreprise).
    if (this.hasSiretInputTarget && siret) this.siretInputTarget.value = this._formatSiret(siret);
    if (this.hasLegalNameInputTarget && name) this.legalNameInputTarget.value = name;

    // Adresse de facturation : non destructif (ne pré-remplir que si vide).
    const line1 = this._buildAddressLine1(siege);
    const line2 = siege.complement_adresse || "";
    const postalCode = siege.code_postal || "";
    const city = siege.libelle_commune || "";
    if (this.hasBillingLine1InputTarget && !this.billingLine1InputTarget.value && line1) {
      this.billingLine1InputTarget.value = line1;
    }
    if (this.hasBillingLine2InputTarget && !this.billingLine2InputTarget.value && line2) {
      this.billingLine2InputTarget.value = line2;
    }
    if (this.hasBillingPostalInputTarget && !this.billingPostalInputTarget.value && postalCode) {
      this.billingPostalInputTarget.value = postalCode;
    }
    if (this.hasBillingCityInputTarget && !this.billingCityInputTarget.value && city) {
      this.billingCityInputTarget.value = city;
    }

    // TVA intracom : uniquement pour b2b, calculée depuis le SIREN.
    if (deducedType === "b2b" && this.hasVatInputTarget && !this.vatInputTarget.value && siren) {
      this.vatInputTarget.value = this._computeVatFr(siren);
    }

    if (this.hasAutofillInputTarget) this.autofillInputTarget.value = "";
    this._hideAutofillSuggestions();
    this._lastAutofillResults = null;
  }

  _buildAddressLine1(siege) {
    const parts = [
      siege?.numero_voie,
      siege?.indice_repetition,
      siege?.type_voie,
      siege?.libelle_voie,
    ].filter(Boolean).map((p) => String(p).trim()).filter((p) => p.length > 0);
    return parts.join(" ").trim();
  }

  _formatSiret(siret) {
    const digits = (siret || "").replace(/\D/g, "");
    if (digits.length !== 14) return digits;
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 9)} ${digits.slice(9)}`;
  }

  _siretDigits(value) {
    return (value || "").replace(/\D/g, "");
  }

  _computeVatFr(siren) {
    const digits = (siren || "").replace(/\D/g, "");
    if (digits.length !== 9) return "";
    const n = Number.parseInt(digits, 10);
    if (!Number.isFinite(n)) return "";
    // Clé de contrôle française : (12 + 3 * (SIREN mod 97)) mod 97
    const key = (12 + 3 * (n % 97)) % 97;
    return `FR${key.toString().padStart(2, "0")}${digits}`;
  }

  // ---------------------------------------------------------------------------
  // Type visibility (b2b/admin → SIRET ; b2c → email principal)
  // ---------------------------------------------------------------------------

  onTypeChange() {
    const type = this._readType();
    this._applyTypeVisibility(type);
    // Changement utilisateur en mode création : nettoyer la TVA quand
    // elle redevient invisible pour ne pas envoyer une valeur stale.
    if (type !== "b2b" && this.hasVatInputTarget) this.vatInputTarget.value = "";
  }

  _applyTypeVisibility(type) {
    const isCompanyType = type === "b2b" || type === "public_administration";
    if (this.hasSiretFieldTarget) this.siretFieldTarget.hidden = !isCompanyType;
    if (this.hasSiretInputTarget) {
      if (isCompanyType) {
        this.siretInputTarget.setAttribute("required", "");
        this.siretInputTarget.setAttribute("aria-required", "true");
      } else {
        this.siretInputTarget.removeAttribute("required");
        this.siretInputTarget.removeAttribute("aria-required");
        this.siretInputTarget.value = "";
      }
    }
    // TVA intracom : visible uniquement pour entreprises (b2b). Cachée pour
    // particuliers et administrations. On ne vide pas la valeur ici (la
    // donnée hydratée d'un client existant doit être préservée — c'est
    // `onTypeChange` qui nettoie en cas de changement utilisateur).
    if (this.hasVatFieldTarget) this.vatFieldTarget.hidden = type !== "b2b";
    // Le libellé "Raison sociale" devient "Nom" pour particuliers et administrations.
    if (this.hasLegalNameLabelTarget) {
      const star = '<span aria-hidden="true">*</span>';
      this.legalNameLabelTarget.innerHTML =
        type === "b2b" ? `Raison sociale ${star}` : `Nom ${star}`;
    }

    // Le pré-remplissage SIRET n'a de sens que pour entreprise/administration.
    // Création : bloc « Remplissage automatique » global. Édition : boutons
    // « Récupérer… » par bloc, eux-mêmes masqués si aucun SIRET n'est saisi.
    if (this._mode === "create" && this.hasAutofillBlockTarget) {
      this.autofillBlockTarget.hidden = !isCompanyType;
    }
    if (this._mode === "edit") this._updateRefetchVisibility(isCompanyType);
  }

  // ---------------------------------------------------------------------------
  // Récupération SIRET par bloc — édition (99.58)
  // ---------------------------------------------------------------------------

  onSiretInput() {
    if (this._mode !== "edit") return;
    this._updateRefetchVisibility();
  }

  _updateRefetchVisibility(isCompanyType = null) {
    const company = isCompanyType !== null
      ? isCompanyType
      : (() => { const t = this._client?.type; return t === "b2b" || t === "public_administration"; })();
    const hasSiret = this._currentSiretDigits().length === 14;
    const show = company && hasSiret;
    if (this.hasIdentityRefetchRowTarget) this.identityRefetchRowTarget.hidden = !show;
    if (this.hasBillingRefetchRowTarget) this.billingRefetchRowTarget.hidden = !show;
  }

  _currentSiretDigits() {
    const fromInput = this.hasSiretInputTarget ? this._siretDigits(this.siretInputTarget.value) : "";
    if (fromInput) return fromInput;
    return this._siretDigits(this._client?.siret || "");
  }

  // Interroge l'API recherche-entreprises par SIRET exact et renvoie le siège
  // INSEE correspondant (repli : 1er résultat). Renvoie null en cas d'absence
  // de SIRET valide ou d'échec réseau/429 (dégradation gracieuse silencieuse).
  async _fetchEstablishmentBySiret() {
    return findEstablishmentBySiret(this._currentSiretDigits(), { include: "siege" });
  }

  async refetchIdentityFromSiret(event) {
    event?.preventDefault?.();
    this._setBusy(this.identityRefetchBtnTarget, true);
    try {
      const result = await this._fetchEstablishmentBySiret();
      if (!result) return;
      const name = result?.nom_raison_sociale || result?.nom_complet || "";
      if (this.hasLegalNameInputTarget && name) this.legalNameInputTarget.value = name;
      // N° TVA intracom : seulement pour les entreprises (b2b). Le Type n'est
      // jamais modifié ici (immuable en édition, sensible pour les requis).
      const type = this._client?.type;
      if (type === "b2b" && this.hasVatInputTarget) {
        const siret = result?.siege?.siret || "";
        const siren = result?.siren || (siret ? siret.slice(0, 9) : "");
        if (siren) this.vatInputTarget.value = this._computeVatFr(siren);
      }
    } finally {
      this._setBusy(this.identityRefetchBtnTarget, false);
    }
  }

  async refetchBillingFromSiret(event) {
    event?.preventDefault?.();
    this._setBusy(this.billingRefetchBtnTarget, true);
    try {
      const result = await this._fetchEstablishmentBySiret();
      if (!result) return;
      const siege = result?.siege || {};
      const line1 = this._buildAddressLine1(siege);
      if (this.hasBillingLine1InputTarget && line1) this.billingLine1InputTarget.value = line1;
      if (this.hasBillingLine2InputTarget) this.billingLine2InputTarget.value = siege.complement_adresse || "";
      if (this.hasBillingPostalInputTarget && siege.code_postal) this.billingPostalInputTarget.value = siege.code_postal;
      if (this.hasBillingCityInputTarget && siege.libelle_commune) this.billingCityInputTarget.value = siege.libelle_commune;
      if (this.hasBillingCountryInputTarget) this.billingCountryInputTarget.value = "FR";
    } finally {
      this._setBusy(this.billingRefetchBtnTarget, false);
    }
  }

  _readType() {
    return this.hasTypeInputTarget ? this.typeInputTarget.value : "b2c";
  }

  // ---------------------------------------------------------------------------
  // Open / close edit-block (calque settings_payment_conditions)
  // ---------------------------------------------------------------------------

  _openBlock(block) {
    if (!block) return;
    block.dataset.editState = "editing";
    const summary = block.querySelector("[data-edit-summary]");
    const form = block.querySelector("[data-edit-form]");
    if (summary) summary.hidden = true;
    if (form) {
      form.hidden = false;
      const first = form.querySelector("input:not([type='hidden']):not([disabled]), select:not([disabled]), textarea");
      if (first) first.focus();
    }
  }

  _closeBlock(block) {
    if (!block) return;
    block.dataset.editState = "idle";
    const summary = block.querySelector("[data-edit-summary]");
    const form = block.querySelector("[data-edit-form]");
    if (form) form.hidden = true;
    if (summary) summary.hidden = false;
  }

  _setBlockSummary(block) {
    if (!block) return;
    block.dataset.editState = "idle";
    const summary = block.querySelector("[data-edit-summary]");
    const form = block.querySelector("[data-edit-form]");
    if (summary) summary.hidden = false;
    if (form) form.hidden = true;
  }

  // ---------------------------------------------------------------------------
  // Error handling
  // ---------------------------------------------------------------------------

  _handleApiError(error, fallbackMsg, blockScope = null) {
    const errors = this._extractFieldErrors(error);
    if (errors.length > 0) {
      for (const { field, message } of errors) {
        const targetName = this._fieldToTarget(field, blockScope);
        if (targetName) {
          this._fieldError(targetName, message);
        }
      }
      // Aussi afficher un toast résumé pour les champs qu'on n'a pas mappés.
      const unmapped = errors.find(({ field }) => !this._fieldToTarget(field, blockScope));
      if (unmapped) this.showError(unmapped.message || fallbackMsg);
      return;
    }
    const msg = this._extractMessage(error) || fallbackMsg;
    if (blockScope === "billing" && this.hasBillingLine1ErrorTarget) {
      this.billingLine1ErrorTarget.textContent = msg;
      this.billingLine1ErrorTarget.hidden = false;
    } else if (blockScope === "shipping" && this.hasShippingLine1ErrorTarget) {
      this.shippingLine1ErrorTarget.textContent = msg;
      this.shippingLine1ErrorTarget.hidden = false;
    } else if (blockScope === "primary" && this.hasPrimaryEmailErrorTarget) {
      this.primaryEmailErrorTarget.textContent = msg;
      this.primaryEmailErrorTarget.hidden = false;
    } else if (blockScope === "newContact" && this.hasNewContactEmailErrorTarget) {
      this.newContactEmailErrorTarget.textContent = msg;
      this.newContactEmailErrorTarget.hidden = false;
    } else {
      this.showError(msg);
    }
  }

  _extractFieldErrors(error) {
    if (!(error instanceof APIError)) return [];
    const data = error.data;
    const errs = data?.error?.errors;
    if (!Array.isArray(errs)) return [];
    return errs
      .filter((e) => e && typeof e === "object" && typeof e.field === "string")
      .map((e) => ({ field: e.field, message: e.message || "Champ invalide." }));
  }

  _extractMessage(error) {
    if (error && typeof error.message === "string") return error.message;
    return null;
  }

  _fieldToTarget(field, scope) {
    // Map des champs backend → noms de "target name" côté front pour
    // `_fieldError`. Le scope permet de distinguer billing/shipping/etc.
    const map = {
      type: "type",
      legalName: "legalName",
      siret: "siret",
      vatIntracom: "vat",
      // Adresses : selon scope
      line1: scope === "shipping" ? "shippingLine1" : "billingLine1",
      postalCode: scope === "shipping" ? "shippingPostal" : "billingPostal",
      city: scope === "shipping" ? "shippingCity" : "billingCity",
      // Contacts : selon scope
      email:
        scope === "primary" ? "primaryEmail"
        : scope === "newContact" ? "newContactEmail"
        : null,
    };
    return map[field] || null;
  }

  _fieldError(name, message) {
    const errTarget = this[`${name}ErrorTarget`];
    const inputTarget = this[`${name}InputTarget`];
    if (errTarget) {
      errTarget.textContent = message;
      errTarget.hidden = false;
    }
    if (inputTarget) inputTarget.setAttribute("aria-invalid", "true");
  }

  _hideError(el) {
    if (el) el.hidden = true;
  }

  _clearAriaInvalid(inputs) {
    for (const el of inputs) {
      if (el) el.removeAttribute("aria-invalid");
    }
  }

  _clearAllErrors() {
    this._clearIdentityErrors();
    this._clearBillingErrors();
    this._clearShippingErrors();
    this._hideError(this.primaryEmailErrorTarget);
    this._hideError(this.newContactEmailErrorTarget);
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  _read(target) {
    if (!target) return "";
    const v = target.value;
    return (typeof v === "string") ? v.trim() : "";
  }

  _setBusy(btn, busy) {
    if (btn) btn.disabled = !!busy;
  }

  _isLuhnValidSiret(value) {
    const digits = (value || "").replace(/\s+/g, "");
    if (!/^\d{14}$/.test(digits)) return false;
    let sum = 0;
    for (let i = 0; i < digits.length; i++) {
      let d = Number.parseInt(digits[i], 10);
      // SIRET : on double les chiffres en position paire en lisant de droite à gauche.
      // Implémentation équivalente : doubler les positions impaires en lisant de gauche à droite.
      if (i % 2 === 0) {
        d *= 2;
        if (d > 9) d -= 9;
      }
      sum += d;
    }
    return sum % 10 === 0;
  }

  _escape(str) {
    if (str === null || str === undefined) return "";
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }
}
